import type {
    NumberRange, QuickSizeFilterOption, UnitFilterRule,
} from '@r6digital/rapidstor-types/react/entities/Journey';
import type UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import Logger from './logging';

type FilterableUnitType = Pick<UnitType, 'width' | 'length' | 'typeName' | 'floor'> & {
    rates: Pick<UnitType['rates'], 'discountedRate'>;
};

interface FilterableDescriptor<U extends FilterableUnitType> {
    _id: string;
    unitTypes: U[];
}

const DIMENSION_EPSILON = 0.001;

const CRITERIA = [
    'area', 'width', 'length', 'price', 'unitTypeNames', 'floors', 'descriptorIds',
] as const;

const inRange = (value: number, { min, max }: NumberRange): boolean => (
    (min === undefined || value >= min) && (max === undefined || value <= max)
);

const matchesDimension = (value: number, targets: number[]): boolean => (
    targets.some((target) => Math.abs(value - target) < DIMENSION_EPSILON)
);

const matchesName = (typeName: string, rule: UnitFilterRule): boolean => {
    const name = typeName.trim().toLowerCase();
    const targets = (rule.unitTypeNames ?? []).map((n) => n.trim().toLowerCase());
    return rule.unitTypeNameMatch === 'contains'
        ? targets.some((target) => name.includes(target))
        : targets.includes(name);
};

/** True when the unit type meets every criterion the rule sets. */
export const matchesRule = (
    unitType: FilterableUnitType,
    descriptorId: string | undefined,
    rule: UnitFilterRule,
): boolean => {
    if (rule.area && !inRange(unitType.width * unitType.length, rule.area)) return false;
    if (rule.width && !matchesDimension(unitType.width, rule.width)) return false;
    if (rule.length && !matchesDimension(unitType.length, rule.length)) return false;
    if (rule.price && !inRange(unitType.rates.discountedRate, rule.price)) return false;
    if (rule.unitTypeNames && !matchesName(unitType.typeName, rule)) return false;
    if (rule.floors && !rule.floors.includes(unitType.floor)) return false;
    if (rule.descriptorIds && (!descriptorId || !rule.descriptorIds.includes(descriptorId))) {
        return false;
    }
    return true;
};

/** Matches any include rule (or there are none) and no exclude rule. */
export const isUnitTypePermitted = (
    unitType: FilterableUnitType,
    descriptorId: string | undefined,
    rules: UnitFilterRule[],
): boolean => {
    const includes = rules.filter((r) => r.mode === 'include');
    const excludes = rules.filter((r) => r.mode === 'exclude');
    const included = includes.length === 0
        || includes.some((r) => matchesRule(unitType, descriptorId, r));
    return included && !excludes.some((r) => matchesRule(unitType, descriptorId, r));
};

/**
 * Narrows each descriptor's unit types to the permitted ones and drops
 * descriptors left empty. Returns the input as-is when there are no rules.
 */
export const applyUnitFilters = <U extends FilterableUnitType, D extends FilterableDescriptor<U>>(
    descriptors: D[],
    rules: UnitFilterRule[] | undefined,
): D[] => {
    if (!rules?.length) return descriptors;

    return descriptors
        .map((d) => ({
            ...d,
            unitTypes: d.unitTypes.filter((ut) => isUnitTypePermitted(ut, d._id, rules)),
        }))
        .filter((d) => d.unitTypes.length > 0);
};

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const isRange = (v: unknown): v is NumberRange => {
    if (typeof v !== 'object' || v === null) return false;
    const { min, max } = v as Record<string, unknown>;
    if (min === undefined && max === undefined) return false;
    if (min !== undefined && !isFiniteNumber(min)) return false;
    if (max !== undefined && !isFiniteNumber(max)) return false;
    return min === undefined || max === undefined || min <= max;
};

const isArrayOf = <T>(v: unknown, guard: (item: unknown) => item is T): v is T[] => (
    Array.isArray(v) && v.length > 0 && v.every(guard)
);

const isString = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

const ruleProblem = (raw: unknown): string | null => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return 'not an object';
    const r = raw as Record<string, unknown>;
    if (r.mode !== 'include' && r.mode !== 'exclude') return `bad mode ${JSON.stringify(r.mode)}`;
    if (r.area !== undefined && !isRange(r.area)) return 'bad area';
    if (r.price !== undefined && !isRange(r.price)) return 'bad price';
    if (r.width !== undefined && !isArrayOf(r.width, isFiniteNumber)) return 'bad width';
    if (r.length !== undefined && !isArrayOf(r.length, isFiniteNumber)) return 'bad length';
    if (r.floors !== undefined && !isArrayOf(r.floors, isFiniteNumber)) return 'bad floors';
    if (r.unitTypeNames !== undefined && !isArrayOf(r.unitTypeNames, isString)) {
        return 'bad unitTypeNames';
    }
    if (r.unitTypeNameMatch !== undefined
        && r.unitTypeNameMatch !== 'exact' && r.unitTypeNameMatch !== 'contains') {
        return 'bad unitTypeNameMatch';
    }
    if (r.descriptorIds !== undefined && !isArrayOf(r.descriptorIds, isString)) {
        return 'bad descriptorIds';
    }
    return null;
};

/**
 * Validates rules from the journey config. A bad rule is dropped whole: keeping
 * it without the bad criterion would widen what it matches.
 */
export const sanitiseUnitFilters = (raw: unknown, source = 'unitFilters'): UnitFilterRule[] => {
    if (raw === undefined || raw === null) return [];
    if (!Array.isArray(raw)) {
        Logger.warn(`[unitFilters] ${source} is not an array, ignored`);
        return [];
    }

    return raw.filter((rule, i): rule is UnitFilterRule => {
        const problem = ruleProblem(rule);
        if (problem) {
            Logger.warn(`[unitFilters] ${source}[${i}] dropped: ${problem}`);
            return false;
        }
        if (!CRITERIA.some((c) => (rule as Record<string, unknown>)[c] !== undefined)) {
            Logger.warn(`[unitFilters] ${source}[${i}] has no criteria and matches every unit type`);
        }
        return true;
    });
};

/** Reserved for the "All" button the listing always shows. */
const RESERVED_QUICK_SIZE_KEY = 'all';

/**
 * Validates quick size buttons from the journey config. An option is dropped
 * when its key or label is bad, its key repeats, or none of its rules survive.
 */
export const sanitiseQuickSizeFilters = (raw: unknown): QuickSizeFilterOption[] => {
    if (raw === undefined || raw === null) return [];
    if (!Array.isArray(raw)) {
        Logger.warn('[unitFilters] quickSizeFilters is not an array, ignored');
        return [];
    }

    const seen = new Set<string>();
    return raw.flatMap((option, i): QuickSizeFilterOption[] => {
        const drop = (problem: string) => {
            Logger.warn(`[unitFilters] quickSizeFilters[${i}] dropped: ${problem}`);
            return [];
        };
        if (typeof option !== 'object' || option === null) return drop('not an object');
        const { key, label, filters } = option as Record<string, unknown>;
        if (!isString(key)) return drop('bad key');
        if (key === RESERVED_QUICK_SIZE_KEY) return drop(`key "${key}" is reserved`);
        if (seen.has(key)) return drop(`duplicate key "${key}"`);
        if (!isString(label)) return drop('bad label');

        const rules = sanitiseUnitFilters(filters, `quickSizeFilters[${i}].filters`);
        if (!rules.length) return drop('no valid filters');

        seen.add(key);
        return [{ key, label, filters: rules }];
    });
};
